/**
 * On-demand customer-session refresh, used to recover from a stale/invalid
 * access token that the cell API Gateway authorizer has rejected (HTTP 403).
 *
 * Direct CORS calls to cell services (assistant-service, provenance-service,
 * etc.) send `realAccessToken` from `AuthContext` as the Bearer. That token is
 * captured at the last `/api/init` load and kept fresh by the proactive refresh
 * loop in `app/page.tsx`. Background-tab `setTimeout` throttling can let that
 * scheduled refresh fire late, stranding a stale token until the next user
 * action — which then 403s. This helper lets any client-side caller force the
 * same `/api/init` refresh the page uses, get a fresh token back to retry the
 * failed request immediately, and (via {@link AUTH_SESSION_REFRESHED_EVENT})
 * push the new token into the rest of the app so subsequent calls also recover.
 *
 * When the underlying Cognito refresh token is itself dead/revoked, `/api/init`
 * answers 401 and we surface a `logout` directive so the caller can bounce the
 * user to sign-out/sign-in instead of looping on the generic retry message.
 */

import { seedGatewaySession } from '@/lib/gateway-fetch';
import { IDLE_SIGN_OUT_HREF, isIdleTimeout } from '@/lib/session/idle-logout';
import type { AuthPool } from '@/lib/auth-pool';
import {
  DISPLACED_LOGIN_HREF,
  displacedLoginHref,
  prepareRefreshPool,
  publishTabTenantFromBody,
  sessionSurface,
  shouldAdoptRefresh,
} from '@/lib/session/tab-sync';

/** Window event fired with the fresh `/api/init` (or `/api/employee/init`) payload after a successful refresh. */
export const AUTH_SESSION_REFRESHED_EVENT = 'tpl-auth-session-refreshed';

/**
 * Window event fired when `/api/init` reports `WRONG_CELL`. The app shell
 * (`app/page.tsx`) is the single owner of that decision (redirect to the correct
 * cell when it differs from the current origin, otherwise show the in-app Access
 * Denied screen), so the deep recovery path defers to it via this event instead
 * of redirecting to sign-in.
 */
export const AUTH_WRONG_CELL_EVENT = 'tpl-auth-wrong-cell';

/**
 * Window event fired when `/api/init` reports `TENANT_LOCKED` (TR-1900 incident
 * lockout). Like {@link AUTH_WRONG_CELL_EVENT}, the app shell (`app/page.tsx`)
 * is the single owner of the resulting screen — a terminal maintenance page with
 * its own recovery poll. A deep-recovery caller that discovers the lock after a
 * cell 403 dispatches this so an already-loaded shell switches to the
 * maintenance screen immediately, instead of showing a broken UI until its next
 * proactive `/api/init` refresh.
 */
export const AUTH_TENANT_LOCKED_EVENT = 'tpl-auth-tenant-locked';

/**
 * App-shell route (`app/page.tsx`) that owns the TR-1900 maintenance screen and
 * its recovery poll. Standalone surfaces (meta-viewer, email-viewer, analytics)
 * read `/api/init` themselves and don't render the maintenance UI; when they see
 * 403 `TENANT_LOCKED` they redirect here so the single, neutral maintenance
 * screen shows and auto-recovers on unlock — never a sign-in bounce that would
 * leak that something is wrong (or, for a locked customer, strand them at the
 * employee login they can't use).
 */
export const TENANT_LOCKED_MAINTENANCE_PATH = '/';

export type RefreshAuthResult =
  | {
      kind: 'refreshed';
      accessToken: string;
      idToken: string | null;
      cellId: string | null;
      tenantId: string | null;
      features: Record<string, unknown>;
    }
  /** Session can't be refreshed (dead refresh token / not authenticated). Navigate to `href`. */
  | { kind: 'logout'; href: string }
  /** `/api/init` returned 403 `WRONG_CELL`. Caller decides redirect vs. Access Denied. */
  | { kind: 'wrong-cell'; correctCellUrl: string | null }
  /**
   * `/api/init` returned 403 `TENANT_LOCKED` (TR-1900 incident lockout). The app
   * shell shows a terminal maintenance screen that polls for recovery; deep
   * recovery callers surface their original error and let the shell take over.
   */
  | { kind: 'tenant-locked' }
  /** Transient failure (network/5xx). Caller should fall back to its normal error path. */
  | { kind: 'unavailable' };

// Two independent coalescing slots — one per Cognito pool.
// Keeping them separate prevents a customer refresh that started before a
// navigation to /employee/* from being handed to employee callers (and vice versa),
// which would deliver the wrong pool's tokens and cause the pool-filtered
// AUTH_SESSION_REFRESHED_EVENT listeners to silently drop the result.
let inFlightCustomer: Promise<RefreshAuthResult> | null = null;
let inFlightEmployee: Promise<RefreshAuthResult> | null = null;

function currentPathname(): string {
  if (typeof window !== 'undefined' && typeof window.location?.pathname === 'string') {
    return window.location.pathname;
  }
  const stub = (globalThis as { location?: { pathname?: string } }).location;
  return typeof stub?.pathname === 'string' ? stub.pathname : '/';
}

/**
 * Apply a successful refresh only when this tab is still on the surface that
 * started it and that pool has not been displaced.
 *
 * Displacement is decided before any decline. A decline used to be
 * `unavailable`, and on `/` `doRefresh` then applied the customer 401: leftover
 * cookies (`hadSession`) became `scope=local`, which clears the employee
 * session and never reaches `/login`.
 *
 * A refresh that left its surface is `unavailable`, not `refreshed`. Home
 * treats `refreshed` as a finished init and will not retry, so the shell would
 * sit on the loading screen. The customer shell does not adopt an employee
 * payload (that would hide a displaced customer flag). That session is still
 * valid, so the caller signs in instead of signing out.
 */
function finishRefresh(
  pool: AuthPool,
  surface: string,
  data: Record<string, unknown>,
  accessToken: string,
  idToken: string | null,
  cellId: string | null,
  tenantId: string | null,
): RefreshAuthResult {
  const blocked = displacedLoginHref(pool);
  if (blocked) return { kind: 'logout', href: blocked };
  const path = currentPathname();
  if (!shouldAdoptRefresh(surface, path)) {
    return { kind: 'unavailable' };
  }
  if (pool === 'employee' && sessionSurface(path) === '/') {
    return { kind: 'logout', href: '/api/auth/sign-in' };
  }
  if (cellId) {
    seedGatewaySession({ accessToken, cellId, tenantId: tenantId || '' });
  }
  if (!publishTabTenantFromBody(pool, data) && displacedLoginHref(pool)) {
    return { kind: 'logout', href: DISPLACED_LOGIN_HREF };
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(AUTH_SESSION_REFRESHED_EVENT, { detail: { ...data, pool } }),
    );
  }
  return { kind: 'refreshed', accessToken, idToken, cellId, tenantId, features: data };
}

/**
 * Try refreshing via the employee pool (`/api/employee/init`).
 *
 * Called when the customer pool returns 401 but we're on an `/employee/*` path —
 * the employee authenticates via the employee (cell-manager) Cognito pool, so the
 * customer pool session may be intentionally absent on this domain. Rather than
 * bouncing the user to the customer-pool sign-in page, we refresh the employee
 * session and continue.
 */
async function tryEmployeePoolRefresh(surface: string): Promise<RefreshAuthResult> {
  let res: Response;
  try {
    res = await fetch('/api/employee/init', { cache: 'no-store', credentials: 'include' });
  } catch {
    return { kind: 'unavailable' };
  }

  if (res.status === 401 && isIdleTimeout(res)) {
    // The employee pool needs this as much as the customer path in doRefresh below: a
    // bounce to /employee/auth/sign-in sails straight back in on the hosted-UI session
    // cookie and defeats the 30-minute policy. See lib/session/idle-logout.ts.
    //
    // TR-3393 made this reachable from one more place: /api/employee/init now enforces
    // idle before its NO_CELL_SELECTED return, so an idle employee sitting on
    // /employee/tenants arrives here with a 401 where they used to get a 403.
    return { kind: 'logout', href: IDLE_SIGN_OUT_HREF };
  }

  if (res.status === 401) {
    // Dead Cognito refresh token — the employee must re-authenticate.
    return { kind: 'logout', href: '/employee/auth/sign-in' };
  }

  if (res.status === 403) {
    const data = (await res.json().catch(() => ({}))) as { code?: string };
    if (data.code === 'NO_CELL_SELECTED') {
      return { kind: 'logout', href: '/employee/tenants' };
    }
    return { kind: 'logout', href: '/employee/auth/sign-in' };
  }

  if (!res.ok) {
    // Transient failure (5xx, rate-limit, gateway timeout, etc.) — mirror the customer
    // path and signal unavailable so the caller falls back to its normal error path
    // instead of bouncing the employee to sign-in on a transient server error.
    return { kind: 'unavailable' };
  }

  const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!data || !data.authenticated) {
    return { kind: 'logout', href: '/employee/auth/sign-in' };
  }

  const tokens = data.tokens as
    | { rawAccessToken?: string; rawIdToken?: string; rawIdTokenData?: string }
    | undefined;
  const accessToken = tokens?.rawAccessToken;
  if (!accessToken) {
    return { kind: 'unavailable' };
  }
  const idToken = tokens?.rawIdToken || tokens?.rawIdTokenData || null;
  const cellId = (data.cellId as string | undefined) ?? null;
  const tenantId = (data.tenantId as string | undefined) ?? null;

  return finishRefresh('employee', surface, data, accessToken, idToken, cellId, tenantId);
}

/** Customer-pool refresh via {@link /api/init}. Only called from {@link refreshAuthSession}. */
async function doRefresh(surface: string, lockoutRecovery = false): Promise<RefreshAuthResult> {
  let res: Response;
  try {
    // `?recovery=1` makes /api/init fail CLOSED on an unknown lockout probe, so a
    // config-service blip keeps the maintenance screen up instead of masquerading
    // as a recovery. See app/api/init/route.ts and app/page.tsx.
    const url = lockoutRecovery ? '/api/init?recovery=1' : '/api/init';
    res = await fetch(url, { cache: 'no-store', credentials: 'include' });
  } catch {
    return { kind: 'unavailable' };
  }

  if (res.status === 401 && isIdleTimeout(res)) {
    // Idle timeout must end the session, not restart it — same treatment tpFetch gives
    // an idle 401 from any other route. See lib/session/idle-logout.ts.
    return { kind: 'logout', href: IDLE_SIGN_OUT_HREF };
  }

  if (res.status === 401) {
    const data = (await res.json().catch(() => ({}))) as { hadSession?: boolean };
    // A tenant-switched employee exists ONLY in the employee pool, so the
    // customer-pool /api/init always 401s for them — even on non-/employee/*
    // surfaces like /analytics/*. Bouncing them to customer sign-in would strand
    // a session that is perfectly refreshable, so fall back to the employee pool
    // (the same customer→employee precedence /api/session/gateway and
    // fetchAnalyticsInit use). A refreshed employee session short-circuits, as
    // does `/login` for a displaced pool and customer sign-in when `/` declines
    // a live employee payload. Any other verdict falls through to the sign-out
    // below so we don't mask a genuine dead customer session.
    //
    // EXCEPT during a lockout-recovery poll: that poll is about THIS customer
    // session's lockout state, and a still-valid employee (tenant-switcher)
    // session is unrelated. Treating that employee `refreshed` as a customer
    // unlock would reload the customer shell, which then ignores the employee
    // payload (the `pool: 'customer'` filter in page.tsx) and — with the customer
    // cookies just cleared by this 401 — leaves the tab stuck on the loading
    // screen instead of the maintenance screen or sign-in. Go straight to logout.
    if (!lockoutRecovery) {
      const viaEmployee = await tryEmployeePoolRefresh(surface);
      // `refreshed` means the employee payload was adopted. A decline because
      // the tab left the surface that started the refresh is `unavailable` and
      // falls through so `/` does not treat it as a finished init.
      if (viaEmployee.kind === 'refreshed') {
        return viaEmployee;
      }
      // Displaced employee pool, or a live employee session the customer shell
      // declined. Both have to win over the `hadSession` sign-out below:
      // `scope=local` clears the employee pool, and a displaced tab belongs on
      // `/login`.
      if (
        viaEmployee.kind === 'logout' &&
        (viaEmployee.href === DISPLACED_LOGIN_HREF || viaEmployee.href === '/api/auth/sign-in')
      ) {
        return viaEmployee;
      }
      // TR-3393: an idle-expired EMPLOYEE session. The fallthrough below would send a CSM whose
      // customer pool never had a session (`hadSession: false`) to /api/auth/sign-in,
      // which does not end the employee hosted-UI session — so they walk straight back
      // into /employee/* unprompted and the 30-minute policy is defeated by the very
      // 401 that was enforcing it. Propagating this cannot mask a dead customer session
      // either: `scope=local` clears BOTH pools, which is what the branch below wanted
      // anyway. Matched on the sign-out href because that is the only idle verdict
      // tryEmployeePoolRefresh returns; its other logouts are /employee/* bounces.
      if (viaEmployee.kind === 'logout' && viaEmployee.href === IDLE_SIGN_OUT_HREF) {
        return viaEmployee;
      }
    }
    // Dead/revoked refresh token: a local sign-out (the refresh token can't
    // drive a GlobalSignOut anyway) mirrors page.tsx and the idle-timeout path.
    return {
      kind: 'logout',
      href: data?.hadSession ? '/api/sign-out?scope=local' : '/api/auth/sign-in',
    };
  }

  if (res.status === 403) {
    const data = (await res.json().catch(() => ({}))) as {
      code?: string;
      correctCellUrl?: string | null;
    };
    if (data?.code === 'WRONG_CELL') {
      // Hand the wrong-cell decision (redirect vs. in-app Access Denied) to the
      // caller so on-demand recovery and the app-shell loop behave identically.
      return {
        kind: 'wrong-cell',
        correctCellUrl: typeof data.correctCellUrl === 'string' ? data.correctCellUrl : null,
      };
    }
    if (data?.code === 'TENANT_LOCKED') {
      // TR-1900 incident lockout. Terminal maintenance screen owned by the app
      // shell; it keeps polling /api/init and recovers automatically on unlock.
      return { kind: 'tenant-locked' };
    }
    return { kind: 'logout', href: '/api/auth/sign-in' };
  }

  if (!res.ok) {
    return { kind: 'unavailable' };
  }

  const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!data || !data.authenticated) {
    return { kind: 'logout', href: '/api/auth/sign-in' };
  }

  const tokens = data.tokens as
    | { rawAccessToken?: string; rawIdToken?: string; rawIdTokenData?: string }
    | undefined;
  const accessToken = tokens?.rawAccessToken;
  if (!accessToken) {
    return { kind: 'unavailable' };
  }
  const idToken = tokens?.rawIdToken || tokens?.rawIdTokenData || null;
  const cellId = (data.cellId as string | undefined) ?? null;
  const tenantId = (data.tenantId as string | undefined) ?? null;

  // pool: 'customer' on the event prevents the employee shell from adopting this
  // payload if an in-flight refresh completes after a navigation to /employee/*.
  return finishRefresh('customer', surface, data, accessToken, idToken, cellId, tenantId);
}

/**
 * Force a token refresh for the pool that owns the current page.
 *
 * - `/employee/*`, and any surface that already claimed the employee pool,
 *   use the employee Cognito pool (`/api/employee/init`).
 * - Every other path uses the customer pool (`/api/init`). A client navigation
 *   onto `/` drops an employee claim made by analytics, admin, or a viewer, so
 *   this call honors a displaced customer flag and does not write that cookie.
 *
 * Each pool has its own coalescing slot so concurrent callers on the same path
 * share one round-trip without risk of getting the other pool's result. A
 * customer refresh that started before a navigation to `/employee/*` is never
 * handed to employee callers and vice versa.
 */
export function refreshAuthSession(
  options?: { lockoutRecovery?: boolean },
): Promise<RefreshAuthResult> {
  const path = currentPathname();
  // Gate the pool this call will apply, not whatever a previous customer-route
  // page claimed. Analytics, admin, and the viewers can claim employee while
  // the URL still looks like a customer route; `/` must still honor a displaced
  // customer flag and must not write that new cookie.
  const refreshPool = prepareRefreshPool(path);
  const blocked = displacedLoginHref(refreshPool);
  if (blocked) return Promise.resolve({ kind: 'logout', href: blocked });
  const surface = sessionSurface(path);

  if (refreshPool === 'employee') {
    if (!inFlightEmployee) {
      inFlightEmployee = tryEmployeePoolRefresh(surface).finally(() => {
        inFlightEmployee = null;
      });
    }
    return inFlightEmployee;
  }

  if (options?.lockoutRecovery === true) {
    // A lockout-recovery poll ALWAYS gets its own fail-CLOSED (`?recovery=1`)
    // refresh and never joins the shared coalescing slot. That slot may hold a
    // fail-OPEN refresh started by a lingering cell-service 403 that resolves as
    // the shell unmounts into the maintenance screen (deep recovery in
    // cors-auth-recovery calls refreshAuthSession() with no flag). If a recovery
    // poll inherited that fail-open promise and config-service happened to be
    // blipping, /api/init would return an authenticated payload and reload the
    // app straight back into the still-locked, all-403 shell. Recovery polls are
    // low-frequency and the app shell (fetchInit) already guards against
    // overlapping polls, so a dedicated refresh is cheap and keeps the fail-open
    // vs. fail-closed modes from ever sharing one round-trip.
    return doRefresh(surface, true);
  }

  if (!inFlightCustomer) {
    inFlightCustomer = doRefresh(surface, false).finally(() => {
      inFlightCustomer = null;
    });
  }
  return inFlightCustomer;
}
